import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import { Send, Handshake, ArrowRight, CheckCircle2 } from "lucide-react";

const acquisitionPerks = [
    "Cash offer up-front — or partner on rev-share",
    "Marketing + analytics from a team that's shipped hits",
    "5-day response, no lock-ins to talk",
];

const About = () => {
    const location = useLocation();
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (location.hash === "#contact") {
            const el = document.getElementById("contact");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [location.hash]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill out every field.");
            return;
        }
        setSubmitting(true);
        try {
            await apiClient.post("/contact", form);
            toast.success("Message sent — we'll reply soon.");
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="about-page" className="pt-28 pb-24">
            <Toaster richColors position="top-right" />
            <div className="max-w-5xl mx-auto px-6 md:px-10">
                <div className="max-w-2xl fade-up">
                    <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-slate-400">About the studio</span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
                        We&apos;re <span className="text-teal-400">Deep Current</span>.
                    </h1>
                    <p className="mt-5 text-slate-300 text-base md:text-lg leading-relaxed">
                        A Roblox-native studio that builds standout experiences and partners with independent creators to grow the ones we love working on.
                    </p>
                </div>

                {/* Game acquisition — short version */}
                <section
                    id="acquisition"
                    data-testid="acquisition-section"
                    className="mt-16 rounded-3xl border border-white/10 bg-[#0F0A14] p-8 md:p-10"
                >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                        <div className="max-w-xl">
                            <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.25em] text-teal-300 px-3 py-1.5 rounded-full border border-teal-500/30 bg-teal-500/10">
                                <Handshake className="w-3.5 h-3.5" /> Game Acquisition
                            </span>
                            <h2 className="mt-4 font-display text-2xl md:text-3xl font-bold text-white leading-tight">
                                Got a Roblox game with traction? Let&apos;s talk.
                            </h2>
                            <ul className="mt-5 space-y-2.5">
                                {acquisitionPerks.map((p, i) => (
                                    <li key={i} data-testid={`acq-perk-${i}`} className="flex items-start gap-2.5 text-sm text-slate-300">
                                        <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 shrink-0" />
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <Button
                            asChild
                            data-testid="acq-cta-pitch"
                            className="rounded-full bg-teal-500 hover:bg-teal-400 text-white h-11 px-5 shadow-[0_0_30px_-8px_rgba(20, 184, 166,0.6)] shrink-0"
                        >
                            <a href="#contact">
                                Pitch your game <ArrowRight className="w-4 h-4" />
                            </a>
                        </Button>
                    </div>
                </section>

                {/* Contact */}
                <div id="contact" className="mt-16 rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F0A14] to-[#0A060E] p-8 md:p-12">
                    <div className="max-w-xl">
                        <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-teal-400">Say hi</span>
                        <h2 className="mt-2 font-display text-3xl md:text-4xl font-bold text-white">Send us a message</h2>
                        <p className="mt-3 text-slate-400 text-sm md:text-base leading-relaxed">
                            Pitching a game, collaborating, or just curious — drop us a line.
                        </p>
                    </div>

                    <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Name</label>
                            <Input
                                data-testid="contact-name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Your name"
                                className="bg-black/40 border-white/10 h-11 text-white placeholder:text-slate-500 focus-visible:ring-teal-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Email</label>
                            <Input
                                data-testid="contact-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="you@studio.com"
                                className="bg-black/40 border-white/10 h-11 text-white placeholder:text-slate-500 focus-visible:ring-teal-500"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">Message</label>
                            <Textarea
                                data-testid="contact-message"
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                placeholder="Tell us about your game, idea, or question…"
                                rows={6}
                                className="bg-black/40 border-white/10 text-white placeholder:text-slate-500 focus-visible:ring-teal-500"
                            />
                        </div>
                        <div className="md:col-span-2 flex justify-end">
                            <Button
                                type="submit"
                                data-testid="contact-submit"
                                disabled={submitting}
                                className="rounded-full h-11 px-6 bg-teal-500 hover:bg-teal-400 text-white shadow-[0_0_30px_-8px_rgba(20, 184, 166,0.6)]"
                            >
                                {submitting ? "Sending…" : (<>Send message <Send className="w-4 h-4" /></>)}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default About;
